import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface ShareButtonProps {
  className?: string;
}

/**
 * Share Button — copies or natively shares a link to the current map view.
 *
 * `useUrlParams` keeps the address bar fully in sync with live app state, so
 * `window.location.href` is the single source of truth for the shareable link.
 * Embed-code generation and per-share option toggles were removed in favor of
 * documented URL params (configured via the Hub "Map Builder").
 */
export function ShareButton({ className = '' }: ShareButtonProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close overlay when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen]);

  // The current URL is exactly the shareable link (kept current by useUrlParams).
  const shareUrl = window.location.href;

  /**
   * Copy the current-view URL to the clipboard
   */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setIsOpen(false);
      }, 1500);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  /**
   * Share via Web Share API (if available), falling back to copy
   */
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('map.shareTitle'),
          text: t('map.shareText'),
          url: window.location.href,
        });
        setIsOpen(false);
      } catch {
        // User cancelled or share failed - no action needed
      }
    } else {
      // Fallback to copy
      await handleCopy();
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Share Button - Icon Only */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`surface-float flex items-center justify-center w-11 h-11 rounded-full hover:scale-105 transition-transform ${className}`}
        aria-label={t('map.shareLocation')}
        title={t('map.shareLocation')}
      >
        <svg
          className="w-5 h-5 text-ink"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
          />
        </svg>
      </button>

      {/* Share Overlay - opens upward (the Filter/Share buttons sit bottom-left) */}
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-80 bg-white rounded-lg shadow-monta-lg border border-monta-gray-200 p-4 z-50">
          <h3 className="text-sm font-semibold text-monta-black mb-3">
            {t('map.shareLocation')}
          </h3>

          {/* Current-view URL */}
          <div className="flex items-start gap-2 p-3 mb-3 bg-monta-gray-50 rounded border border-monta-gray-200">
            <input
              type="text"
              value={shareUrl}
              readOnly
              aria-label={t('map.shareLocation')}
              className="flex-1 text-xs font-mono text-monta-gray-700 bg-transparent border-none outline-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleCopy}
              className="flex-1 px-3 py-2 bg-monta-gray-100 text-monta-black rounded-lg text-sm font-medium hover:bg-monta-gray-200 transition-colors"
            >
              {copied ? (
                <>
                  <svg
                    className="w-4 h-4 inline-block mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {t('common.copied')}
                </>
              ) : (
                t('common.copyLink')
              )}
            </button>
            {typeof navigator !== 'undefined' && 'share' in navigator && (
              <button
                onClick={handleShare}
                className="flex-1 px-3 py-2 bg-monta-black text-white rounded-lg text-sm font-medium hover:bg-monta-gray-800 transition-colors"
              >
                {t('common.share')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
